import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { formatDate } from '../../common/SharedFunctions';
import { BfiDataGrid, Column, MasterDetail, LoanCommitOrApprovalColumn } from '../../common/SharedComponents';
import { onCityCellRender, onStateCellRender } from '../../loans/common/LoanFunctions';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { GridSettingType, LoanStatusOptions } from '@apply/constants';
import { getLoanTypeShortName, OnOpenLoanDetail, onRowPrepared } from '../../loans/common/LoanFunctions';
import { LoanType } from '@apply/constants';
import { onActionCellRender, onRenderPortfolioGrid } from '../../loans/common/LoanFunctions';
import { LoanModals } from '../../loans/common/LoanComponents';
import { LoanStatusType } from '../../loans/common/LoanConstants';

const EntityLoansTable = ({ entityId, masterNotes, gridStore, isClosed }) => {
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const location = useLocation();

    const [dataStore, set_dataStore] = useState(gridStore?.store);
    const [statusId, set_statusId] = useState(LoanStatusType.Approved);

    const dataFunction = (criteria) => {
        return loanApi.getGrid_EntityLoans(entityId, masterNotes, criteria, isClosed);
    };

    useEffect(() => {
        if (isClosed) {
            set_statusId(LoanStatusType.Outstanding);
        }
    }, [isClosed]);

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, null);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    const approvalDataCellRender = (e) => {
        return (
            <>
                {!e.data.approvalDate && 'Not Set'}
                {e.data.approvalDate && formatDate(e.data.approvalDate)}
            </>
        );
    };

    const noteDateDataCellRender = (e) => {
        return (
            <>
                {!e.data.noteDate && 'Not Set'}
                {e.data.noteDate && formatDate(e.data.noteDate)}{' '}
            </>
        );
    };

    const onLoanStatusRender = (e) => {
        const status = LoanStatusOptions.find((s) => s.id === e.value);
        return status?.shortName;
    };

    const onLoanTypeCellRender = (e) => {
        if (e.data.isRental) {
            return getLoanTypeShortName(LoanType.Rental);
        }
        return getLoanTypeShortName(e.data.loanTypeId);
    };

    if (!entityId) {
        return null;
    }

    return (
        <>
            <BfiDataGrid
                gridType={GridSettingType.EntityLoans}
                dataStore={dataStore}
                applicationInsights={applicationInsights}
                noDataText='No Loans Found'
                onRowPrepared={onRowPrepared}
                onCellDblClick={(e) => OnOpenLoanDetail(e, location)}
            >
                <Column
                    dataField='loanTypeId'
                    caption='Type'
                    cellRender={onLoanTypeCellRender}
                    alignment='left'
                    width={80}
                />
                <Column
                    dataField='bfiNumber'
                    caption='BFI #'
                    dataType='string'
                    width={100}
                />
                <Column
                    dataField='loanNumber'
                    caption='Loan #'
                    dataType='string'
                    width={80}
                />
                <Column
                    dataField='propertyCity'
                    caption='City'
                    cellRender={onCityCellRender}
                />
                <Column
                    dataField='propertyState'
                    caption='State'
                    cellRender={onStateCellRender}
                    width={70}
                />
                {isClosed && (
                    <Column
                        dataField='noteDate'
                        caption='Note Date'
                        cellRender={noteDateDataCellRender}
                        width={120}
                    />
                )}
                {!isClosed && (
                    <Column
                        dataField='approvalDate'
                        caption='Approved'
                        cellRender={approvalDataCellRender}
                        width={100}
                    />
                )}

                {masterNotes && (
                    <Column
                        dataField='commitment'
                        caption={'Total Credit Line'}
                        dataType='number'
                        format='currency'
                        width={90}
                        alignment={'left'}
                    />
                )}
                {!masterNotes && LoanCommitOrApprovalColumn({ statusId })}
                <Column
                    dataField='broker'
                    dataType='string'
                    width={120}
                />
                <Column
                    dataField='statusId'
                    caption='Status'
                    dataType='string'
                    width={100}
                    cellRender={onLoanStatusRender}
                />
                <Column
                    alignment='center'
                    cellRender={onActionCellRender}
                    width={50}
                    allowReordering={false}
                    allowSorting={false}
                    allowResizing={false}
                    type='actions'
                    fixed={true}
                    fixedPosition={'right'}
                />
                <MasterDetail
                    enabled={true}
                    render={(e) => onRenderPortfolioGrid(e, statusId)}
                />
            </BfiDataGrid>
            <LoanModals />
        </>
    );
};

export default EntityLoansTable;
